import { type SledTeam, ROOT_SCOPE_NUMBER, ROOT_SCOPE_NAME } from '@sdd-maturity/shared-types';

/**
 * URL-canonical navigation (FR-1). Typed per-tier route segments encode the full
 * location in the path; scope is resolved from the URL against the cached
 * `getAllTeams()` tree rather than from `sessionStorage`/`TeamContext`.
 *
 * Segment index mirrors the tier number (1-4): Portfolio → Delivery Stream →
 * Release Train → Team. Browser routes are intentionally decoupled from the
 * backend `/teams/:teamNumber/...` API routes.
 */
export const SEGMENTS = ['portfolios', 'delivery-streams', 'release-trains', 'teams'] as const;
export type ScopeSegment = (typeof SEGMENTS)[number];

/**
 * The synthetic "Cox Automotive" root scope (tier 0). It owns no real SLED node —
 * its children are all tier-1 portfolios — and lives at the bare lens routes `/`,
 * `/discovery`, `/delivery`. Discriminate it by {@link isRootNode} (its sentinel
 * number), never by `tier` (0 is also `parseLeafEntity`'s non-scope leaf marker).
 */
export const ROOT_NUMBER = ROOT_SCOPE_NUMBER;
export const ROOT_NODE: SledTeam = {
  number: ROOT_NUMBER,
  name: ROOT_SCOPE_NAME,
  tier: 0,
  tierLabel: '',
  level: '0',
  active: true,
};
export function isRootNode(node?: { number: string } | null): boolean {
  return !!node && node.number === ROOT_NUMBER;
}

/** The view (lens) carried as the last path segment of a scope route. */
export type ScopeLens = 'overview' | 'discovery' | 'delivery';
export const LENSES: readonly ScopeLens[] = ['overview', 'discovery', 'delivery'];

/** AC-1.1: tier (1-4) → typed segment. `undefined` for an out-of-range tier. */
export function segmentForTier(tier: number): ScopeSegment | undefined {
  return SEGMENTS[tier - 1];
}

/** Inverse of {@link segmentForTier}: typed segment → tier (1-4). `undefined` when unknown. */
export function tierForSegment(segment: string): number | undefined {
  const idx = SEGMENTS.indexOf(segment as ScopeSegment);
  return idx === -1 ? undefined : idx + 1;
}

/** AC-1.5 guard: does this typed segment correspond to the node's tier? */
export function segmentMatchesTier(segment: string, tier: number): boolean {
  return tierForSegment(segment) === tier;
}

/** Normalize a raw `:lens` path param to a known lens, defaulting to `overview`. */
export function normalizeLens(raw?: string): ScopeLens {
  return raw && (LENSES as readonly string[]).includes(raw) ? (raw as ScopeLens) : 'overview';
}

/**
 * Build a canonical typed scope URL for a node, e.g. `/teams/T103/discovery`.
 * Falls back to the `teams` segment if the tier is unknown so the result is
 * always a structurally valid path.
 */
export function buildScopePath(node: { tier: number; number: string }, lens: ScopeLens = 'overview'): string {
  // The root scope lives at the bare lens routes — `/`, `/discovery`, `/delivery` —
  // not a typed `/{segment}/{number}` path (segmentForTier(0) is undefined).
  if (node.number === ROOT_NUMBER) return lens === 'overview' ? '/' : `/${lens}`;
  const segment = segmentForTier(node.tier) ?? 'teams';
  return `/${segment}/${encodeURIComponent(node.number)}/${lens}`;
}

/**
 * FR-5: canonical leaf URL for a Discovery feature, owning team in the path —
 * `/teams/:teamNumber/discovery/features/:formattedId`. Leaves hang off teams only.
 */
export function buildFeaturePath(teamNumber: string, formattedId: string): string {
  return `/teams/${encodeURIComponent(teamNumber)}/discovery/features/${encodeURIComponent(formattedId)}`;
}

/**
 * FR-5: canonical leaf URL for a Delivery repo, owning team in the path —
 * `/teams/:teamNumber/delivery/repos/:org/:repo`. Closes the repo missing-team gap.
 */
export function buildRepoPath(teamNumber: string, org: string, repo: string): string {
  return `/teams/${encodeURIComponent(teamNumber)}/delivery/repos/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`;
}

/**
 * Result of classifying a browser pathname for page-header purposes (FR-2).
 * `root` — the synthetic "Cox Automotive" scope at the bare lens routes `/`,
 * `/discovery`, `/delivery` (children are all portfolios — see {@link ROOT_NODE});
 * `typed` — a `/{segment}/{number}/{lens?}` scope route, including the canonical
 * leaf routes `/teams/:n/discovery/features/:id` and `/teams/:n/delivery/repos/:org/:repo`
 * (FR-5: the team is the path's node, the leaf entity is the tail — see {@link parseLeafEntity});
 * `none` — everything else (e.g. `/calibration`).
 */
export type ParsedScopePath =
  | { kind: 'root'; lens: ScopeLens }
  | { kind: 'typed'; segment: ScopeSegment; number: string; lensParam?: string }
  | { kind: 'none' };

/**
 * Classify a pathname for the node-scoped page header (FR-2). The typed scope
 * routes carry a node `number` in the path — including the canonical FR-5 leaf
 * routes (`/teams/:n/discovery/features/:id`, `/teams/:n/delivery/repos/:org/:repo`),
 * whose owning team is the path node and whose `:lens` segment drives the tab. The
 * bare single-segment lens routes (`/`, `/discovery`, `/delivery`) are the root
 * "Cox Automotive" scope. Anything else (`/calibration`) maps to `none`.
 */
export function parseScopePath(pathname: string): ParsedScopePath {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const [head, number, lensParam] = parts;
  if (head && number && SEGMENTS.includes(head as ScopeSegment)) {
    return { kind: 'typed', segment: head as ScopeSegment, number: decodeURIComponent(number), lensParam };
  }
  // Root-scope lens routes are single-segment, so leaf/typed routes never match
  // here. `/` splits to `['']`; `/delivery-streams/...` is caught above first.
  if (parts.length === 1 && (head === '' || head === 'discovery' || head === 'delivery')) {
    const lens: ScopeLens = head === 'discovery' ? 'discovery' : head === 'delivery' ? 'delivery' : 'overview';
    return { kind: 'root', lens };
  }
  return { kind: 'none' };
}

/**
 * FR-2: the lens (tab) a pathname represents, always one of the three tabs.
 * Unlike `modeFromPath` (which is 2-way and defaults to `delivery`), this
 * distinguishes `overview` so it can light a 3-way tab strip. A typed route's
 * `:lens` segment wins; otherwise the legacy/leaf paths map by prefix, with the
 * whole-segment `/delivery` test guarding against the `/delivery-streams/` tier
 * segment. Anything unmatched (notably `/`) is `overview`.
 */
export function lensFromPath(pathname: string, lensParam?: string): ScopeLens {
  if (lensParam) return normalizeLens(lensParam);
  if (pathname.startsWith('/discovery') || pathname.startsWith('/features')) return 'discovery';
  if (/\/delivery(\/|$)/.test(pathname) || pathname.startsWith('/repos')) return 'delivery';
  return 'overview';
}

/**
 * AC-1.2 + AD-5: resolve a node by `number` against the cached tree, tier-aware.
 * When `expectedTier` is given and a same-number node exists at that tier, prefer
 * it — so a cross-tier `number` collision (OQ-1) can never mis-render; the URL
 * segment disambiguates. Falls back to the first match (a wrong segment then
 * triggers the AC-1.5 redirect). Returns `null` when no node has that number.
 */
export function resolveNodeByNumber(teams: SledTeam[], number: string, expectedTier?: number): SledTeam | null {
  const matches = teams.filter((t) => t.number === number);
  if (matches.length === 0) return null;
  if (expectedTier !== undefined) {
    const exact = matches.find((t) => t.tier === expectedTier);
    if (exact) return exact;
  }
  return matches[0] ?? null;
}

/**
 * FR-3: one segment of the page-header breadcrumb. A scope-node segment carries its
 * real `tier` (1-4) and `number` so the breadcrumb can build a typed-route link; the
 * terminal leaf segment (feature/repo) uses `tier: 0` as a non-tier marker — it is
 * always rendered last (plain text, the current location) so its tier never builds a link.
 */
export interface BreadcrumbSegment {
  number: string;
  name: string;
  tier: number;
  tierLabel?: string;
}

/**
 * AC-3.1/AC-3.2: build the breadcrumb scope chain for a node — the ordered trail from
 * the root portfolio down to the node itself (node last) — by walking `parentNumber`
 * up the cached tree. Tier-bounded (≤4 hops) to guard against cycles/missing links,
 * mirroring `ancestorTrail` in `SearchLauncher`.
 */
export function buildBreadcrumbPath(node: SledTeam, teams: SledTeam[]): BreadcrumbSegment[] {
  const byNumber = new Map(teams.map((t) => [t.number, t]));
  const toSegment = (t: SledTeam): BreadcrumbSegment => ({
    number: t.number,
    name: t.name,
    tier: t.tier,
    tierLabel: t.tierLabel,
  });

  const chain: BreadcrumbSegment[] = [toSegment(node)];
  let current = node.parentNumber ? byNumber.get(node.parentNumber) : undefined;
  let guard = 0;
  while (current && guard < 4) {
    chain.unshift(toSegment(current));
    current = current.parentNumber ? byNumber.get(current.parentNumber) : undefined;
    guard += 1;
  }
  return chain;
}

/**
 * AC-3.3: the terminal leaf segment for a leaf pathname, or `null` when the path is
 * not a leaf. Features carry their `formattedId` in the path (the human name needs a
 * per-team fetch the breadcrumb doesn't do); repos carry `org/repo`. `tier: 0` marks
 * these as non-scope nodes — they render as the last, non-navigable segment.
 *
 * Recognizes both the canonical FR-5 typed leaves (`/teams/:n/discovery/features/:id`,
 * `/teams/:n/delivery/repos/:org/:repo`), where the entity is the path tail, and the
 * legacy index-0 leaves (`/features/:id`, `/repos/:org/:repo`) still live until TASK-6.
 */
export function parseLeafEntity(pathname: string): BreadcrumbSegment | null {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/');
  // Strip a leading typed team scope (`/teams/:number/:lens`) so the same entity
  // matching covers both the canonical FR-5 leaves and the legacy index-0 leaves.
  const tail =
    parts[0] === 'teams' && (parts[2] === 'discovery' || parts[2] === 'delivery')
      ? parts.slice(3)
      : parts;
  const [head, a, b] = tail;
  if (head === 'features' && a) {
    const formattedId = decodeURIComponent(a);
    return { number: formattedId, name: formattedId, tier: 0, tierLabel: 'Feature' };
  }
  if (head === 'repos' && a && b) {
    const org = decodeURIComponent(a);
    const repo = decodeURIComponent(b);
    return { number: `${org}/${repo}`, name: repo, tier: 0, tierLabel: 'Repository' };
  }
  return null;
}
